'use client'

import React, { useRef, FormEvent, useState } from 'react'
import { AlertCircle, CheckCircle, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

const GOOGLE_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbxoljv_wZhcKGIlyO58yW9vgxcwp3sJIKBfGEu-eLrGJoUP2T64eafHWqrS_ub08k1p/exec'

interface ScriptResponse {
  result: 'success' | 'error'
  row?: number
  error?: any
}

interface HostCourseFormProps {
  title?: string
  onSuccess?: () => void
  isModal?: boolean
  onClose?: () => void
  sheetName?: string
}

export function HostCourseForm({
  title = 'Interested in Hosting a Course?',
  onSuccess,
  isModal = false,
  onClose,
  sheetName = 'HostYourCourse',
}: HostCourseFormProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!formRef.current || isSubmitting) return

    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')

    let response: Response | null = null

    try {
      const formData = new FormData(formRef.current)
      response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        body: formData,
      })

      console.log('Fetch Response Status:', response.status)

      let data: ScriptResponse | null = null
      let responseText = ''
      try {
        responseText = await response.text()
        console.log('Raw Response Text:', responseText)
        data = JSON.parse(responseText)
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError)
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}: ${responseText || 'No response body'}`)
        }
        throw new Error(`Received OK status (${response.status}) but failed to parse JSON response.`)
      }

      console.log('Parsed Response Data:', data)

      if (response.ok && data?.result === 'success') {
        setSubmitStatus('success')
        setSubmitMessage(
          "Thank you for your interest! We'll be in touch soon.",
        )
        formRef.current.reset()
        if (onSuccess) onSuccess()
        if (isModal && onClose) {
          setTimeout(onClose, 2000)
        }
      } else {
        const scriptErrorDetail = data?.error
        const errorMessage = scriptErrorDetail?.message || (typeof scriptErrorDetail === 'string' ? scriptErrorDetail : JSON.stringify(scriptErrorDetail)) || 'Script reported an unknown error.'
        console.error('Submission Error (Script/Data):', {
          status: response.status,
          result: data?.result,
          errorDetail: scriptErrorDetail,
          fullData: data
        })
        setSubmitStatus('error')
        setSubmitMessage(`Submission failed: ${errorMessage}`)
      }
    } catch (error) {
      console.error('Fetch/Process Error:', error)
      setSubmitStatus('error')
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.'
      setSubmitMessage(
        `An error occurred: ${message} (Status: ${response?.status ?? 'N/A'})`
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const formContent = (
    <>
      <h2 className="mb-6 text-center text-2xl font-bold text-gray-800">
        {title}
      </h2>

      {submitStatus === 'success' && (
        <div className="mb-6 flex items-start rounded-md border-l-4 border-green-500 bg-green-50 p-4 text-green-700">
          <CheckCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">{submitMessage}</p>
          </div>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="mb-6 flex items-start rounded-md border-l-4 border-red-500 bg-red-50 p-4 text-red-700">
          <AlertCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">{submitMessage}</p>
          </div>
        </div>
      )}

      {submitStatus !== 'success' && (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
          <input type="hidden" name="sheet" value={sheetName} />

          <div className="space-y-1">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className="w-full rounded-md border border-gray-300 p-2.5 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
              disabled={isSubmitting}
              placeholder="Your Full Name"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="w-full rounded-md border border-gray-300 p-2.5 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
              disabled={isSubmitting}
              placeholder="your.email@example.com"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700"
            >
              Phone (Optional)
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              className="w-full rounded-md border border-gray-300 p-2.5 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
              disabled={isSubmitting}
              placeholder="+1 234 567 890"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="courseTopic"
              className="block text-sm font-medium text-gray-700"
            >
              Course Topic / Idea <span className="text-red-500">*</span>
            </label>
            <textarea
              id="courseTopic"
              name="courseTopic"
              required
              minLength={5}
              rows={3}
              className="w-full rounded-md border border-gray-300 p-2.5 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
              disabled={isSubmitting}
              placeholder="Briefly describe the course you'd like to host"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="experience"
              className="block text-sm font-medium text-gray-700"
            >
              Your Experience (Optional)
            </label>
            <textarea
              id="experience"
              name="experience"
              rows={3}
              className="w-full rounded-md border border-gray-300 p-2.5 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
              disabled={isSubmitting}
              placeholder="Tell us briefly about your relevant experience"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center rounded-md bg-blue-600 p-3 font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting && (
                <svg
                  className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isSubmitting ? 'Submitting...' : 'Submit Interest'}
            </button>
          </div>
        </form>
      )}
    </>
  )

  if (isModal) {
    return (
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-2xl rounded-lg border border-gray-100 bg-white p-6 shadow-md"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {onClose && (
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                aria-label="Close form"
              >
                <X size={20} />
              </button>
            )}
            {formContent}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    )
  }

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-md">
      {formContent}
    </div>
  )
} 